import type { ReactNode } from 'react';
import { PlusIcon } from '@phosphor-icons/react';
import { Button, SearchField } from '@skupreme/shared/ui';
import useUrlPermission from '@hooks/url-permission';
import { LinkButton } from '../LinkButton';
import { PermissionGate } from '../PermissionGate';

type SearchProps = {
  search: string;
  onSearchChange: (search: string) => void;
  loading: boolean;
};

type ActionProps = {
  /** The list's filter control. */
  filters: ReactNode;
  onExport: () => void;
  onAddProvider: () => void;
  /** A summary account reads its brands' providers but adds none. */
  isSummaryView: boolean;
};

/* Sits in the page header, next to the title. EN-4200: the V2 list is
   read-only, so it gets the search alone (and, EN-4396, the division filter
   at the account level). */
type ProvidersToolbarProps = SearchProps &
  (
    | { readOnly: true; filters?: ReactNode }
    | ({ readOnly?: false } & ActionProps)
  );

export function ProvidersToolbar(props: ProvidersToolbarProps) {
  const { search, onSearchChange, loading } = props;
  return (
    <div className="sk:flex sk:items-center sk:gap-2">
      <SearchField
        aria-label="Search providers"
        placeholder="Search providers..."
        isDisabled={loading}
        isLoading={loading}
        onChange={onSearchChange}
        value={search}
        className="sk:w-60 sk:max-w-full"
      />
      {props.readOnly ? props.filters : <ProviderActions {...props} />}
    </div>
  );
}

function ProviderActions({
  filters,
  onExport,
  onAddProvider,
  isSummaryView,
}: ActionProps) {
  const { disabled, hidden } = useUrlPermission();

  return (
    <>
      {filters}
      <Button onPress={onExport}>Export</Button>
      {isSummaryView ? null : (
        <>
          <PermissionGate disabled={disabled} hidden={hidden}>
            <LinkButton to="/app/scm/shipments/create" disabled={disabled}>
              <PlusIcon size={18} /> Shipment
            </LinkButton>
          </PermissionGate>
          <PermissionGate
            disabled={disabled}
            hidden={hidden}
            placement="bottom"
          >
            <Button
              variant="primary"
              isDisabled={disabled}
              onPress={onAddProvider}
            >
              <PlusIcon size={18} /> Add Provider
            </Button>
          </PermissionGate>
        </>
      )}
    </>
  );
}
